import { Link } from 'react-router-dom';
import { Card, CardContent, Badge, PlatformMark } from '../../ui';
import { MoodCell, STATUS_LABEL } from '../heartbeat';
import { useHeartbeatIndex } from '../../../services/queries';
import { relativeTime } from '../../../utils/dates';
import PATHS, { ROUTE_PATTERNS, buildRoute } from '../../../constants/Paths';

const ACTIVE = ['queued', 'collecting', 'labeling'];

/**
 * How the internet feels, on the screen everyone opens first.
 *
 * Heartbeat shipped with its only entry point in the sidebar: a user landing on
 * the dashboard got no signal it existed, no sense of whether anything was
 * listening, and no reason to click it.
 *
 * Reads the index endpoint that already exists - no new backend.
 */
export function HeartbeatBand() {
	const { data, isLoading } = useHeartbeatIndex({ limit: 4, sort: 'recent' });
	const runs = data?.runs || [];

	if (isLoading || runs.length === 0) return null;

	const listening = runs.filter((r) => ACTIVE.includes(r.status)).length;

	return (
		<Card>
			<CardContent>
				<div className="flex flex-wrap items-baseline justify-between gap-2">
					<h2 className="text-base font-semibold text-foreground">Heartbeat</h2>
					<div className="flex items-center gap-3 text-sm">
						{listening > 0 && (
							<span className="text-brand-700">{listening} listening now</span>
						)}
						<Link to={PATHS.HEARTBEAT} className="font-medium text-brand-600 hover:underline">
							All pulses →
						</Link>
					</div>
				</div>

				<ul className="mt-3 divide-y divide-border">
					{runs.map((r) => {
						const acc = r.account || {};
						const to = acc._id
							? buildRoute(ROUTE_PATTERNS.HEARTBEAT_RUN, { accountId: acc._id, runId: r._id })
							: null;
						const inner = (
							<>
								<div className="relative shrink-0">
									<img src={acc.avatar || 'https://placehold.co/28x28'} alt="" className="h-7 w-7 rounded-full bg-muted object-cover" />
									<PlatformMark platform={r.platform} size="xs" className="absolute -bottom-0.5 -right-0.5 ring-2 ring-white" />
								</div>
								<div className="min-w-0 flex-1">
									<div className="truncate text-sm font-medium text-foreground">@{acc.handle || 'unknown'}</div>
									<div className="truncate text-xs text-muted-foreground">
										{r.client?.name}
										{r.createdAt ? ` · ${relativeTime(r.createdAt)}` : ''}
									</div>
								</div>
							</>
						);

						return (
							<li key={r._id} className="flex flex-wrap items-center gap-3 py-2.5">
								{to ? (
									<Link to={to} className="flex min-w-0 flex-1 items-center gap-3 hover:underline">{inner}</Link>
								) : (
									<div className="flex min-w-0 flex-1 items-center gap-3">{inner}</div>
								)}

								{ACTIVE.includes(r.status) ? (
									<Badge variant="outline" size="sm">{STATUS_LABEL[r.status] || r.status}</Badge>
								) : (
									<MoodCell totals={r.totals} status={r.status} />
								)}
							</li>
						);
					})}
				</ul>
			</CardContent>
		</Card>
	);
}
